import { Children, cloneElement, isValidElement } from 'react'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import { BLOCKS, MARKS, INLINES } from '@contentful/rich-text-types'
import { MediaImageStandard, SuperScript } from 'components/Contentful'
import { _get } from 'utils/shared'
import { BlockQuote } from 'components/BlockQuote'
import { getAssetUrl } from 'utils/assetUrl'
import classNames from 'classnames'
import { NO_CRAWL_ID_PLACEHOLDER_REGEX } from 'constants/regex'
import { VideoDialogClient } from './VideoDialogClient'
import { ScrollLinkClient, ScrollToMarker } from './ScrollLinkClient'
import { UncrawlableLinkClient } from './UncrawlableLinkClient'
import { HyperLinkClient } from './HyperLinkClient'
import { ClientSideTypographyClient } from './ClientSideTypographyClient'
import {
  AscendResponsiveTypography,
  AscendTypography,
  AscendLink,
  AscendDivider,
  AscendTable,
  AscendTableContainer,
  AscendTableRow,
  AscendTableCell,
  AscendTableHead,
  AscendTableBody,
  AscendPaper,
} from './AscendResponsiveTypography'
import { sanitizeSvg } from 'utils/sanitize/sanitize'
import { PdfViewerClient } from '@/components/PdfViewer/PdfViewerClient'

/**
 * Contentful RichText places empty paragraph after elements
 * like headings and lists if they are last item in the RichText box
 * @param {Array} content
 * @returns {Array} filtered content
 */

const replaceInlineEntries = (obj1, obj2) => {
  if (obj1?.content?.length) {
    return { ...obj1, content: obj1.content.map((subItem) => replaceInlineEntries(subItem, obj2)) }
  }
  for (const inlineItem of obj2.entries.inline) {
    if (
      inlineItem?.sys?.id === obj1?.data?.target?.sys?.id ||
      inlineItem?.id === obj1?.data?.target?.sys?.id
    ) {
      return { ...obj1, data: { ...inlineItem, id: inlineItem?.sys?.id } }
    }
  }
  return obj1
}

const explicitOverrides = [
  'bodyLg',
  'bodyMd',
  'bodySm',
  'bodyXs',
  'displayLg',
  'displayMd',
  'displaySm',
  'headingLg',
  'headingMd',
  'headingSm',
  'labelMd',
]

const BALANCED_HEADING_COMPONENTS = ['h1', 'h2', 'h3']

const withHeadingBalance = (component, className) =>
  BALANCED_HEADING_COMPONENTS.includes(component)
    ? classNames(className, 'text-balance')
    : className

const replaceBlockEntries = (obj1, obj2) => {
  if (Array.isArray(obj1?.content)) {
    return { ...obj1, content: obj1.content.map((subItem) => replaceBlockEntries(subItem, obj2)) }
  }
  for (const blockItem of obj2.entries.block) {
    if (
      blockItem?.sys?.id === obj1?.data?.target?.sys?.id ||
      blockItem?.id === obj1?.data?.target?.sys?.id
    ) {
      return { ...obj1, data: { ...blockItem, id: blockItem?.sys?.id } }
    }
  }
  return obj1
}

const replaceInlineHyperLink = (obj1, obj2) => {
  if (obj1?.data?.target?.sys?.id) {
    for (const inlineHyperlink of obj2.assets.hyperlink) {
      if (
        inlineHyperlink?.sys?.id === obj1?.data?.target?.sys?.id ||
        inlineHyperlink?.id === obj1?.data?.target?.sys?.id
      ) {
        return { ...obj1, data: { ...inlineHyperlink, id: inlineHyperlink?.sys?.id } }
      }
    }
  }
  if (obj1?.content?.length) {
    return {
      ...obj1,
      content: obj1.content.map((subItem) => replaceInlineHyperLink(subItem, obj2)),
    }
  }
  return obj1
}

/**
 * Checks if a given node is an empty text node.
 * An empty text node is defined as a node that:
 * - Has a `nodeType` property of type 'string' that matches the word "paragraph" (case-insensitive).
 * - Contains only one item in its `content` array.
 * - The single item in the `content` array is of type 'text'.
 * - The value of the single 'text' type item is an empty string.
 *
 * @param {Object} node - The node to check.
 * @returns {boolean} - Returns `true` if the node is an empty text node, otherwise `false`.
 */
const isEmpyTextNode = (node) => {
  return (
    typeof node.nodeType === 'string' &&
    node.nodeType.match(/paragraph/i) &&
    node.content.length === 1 &&
    node.content[0].nodeType === 'text' &&
    node.content[0].value === ''
  )
}

const removeTrailingReturnChar = (content) => {
  if (!Array.isArray(content)) {
    // Gracefully fail
    return content
  }
  if (content.length <= 1) {
    return content
  }
  const lastItem = content[content.length - 1]
  const prevLastItem = content[content.length - 2]
  //Only do the return char removal if the last item is an empty text node and the previous one is not, this is to avoid hydration error.
  if (isEmpyTextNode(lastItem) && !isEmpyTextNode(prevLastItem)) {
    return content.slice(0, -1)
  }
  return content
}

/**
 * Filters out block-level elements when inline mode is used to prevent hydration errors
 * @param {Array} content - The content array to filter
 * @param {boolean} inline - Whether inline mode is enabled
 * @returns {Array} Filtered content array
 */
const filterInlineContent = (content, inline) => {
  if (!inline || !Array.isArray(content)) {
    return content
  }

  // Block-level elements that should be filtered out in inline mode
  const blockElements = [
    'heading-1',
    'heading-2',
    'heading-3',
    'heading-4',
    'heading-5',
    'heading-6',
    'unordered-list',
    'ordered-list',
    'list-item',
    'hr',
    'table',
    'table-row',
    'table-cell',
    'table-header-cell',
    'blockquote',
    'embedded-asset',
  ]

  return content.filter((item) => {
    if (item.nodeType && blockElements.includes(item.nodeType)) {
      console.warn(`Typography: Skipping block-level element "${item.nodeType}" in inline mode`)
      return false
    }
    return true
  })
}

/**
 * Always returns textContent with content array as a prop in the shape
 * @param {Object} content
 * @returns {Object} textContent: {content: []}
 */
const handleContentToMapper = (content = {}, inline = false) => {
  let textContent = {}
  if (Object.hasOwnProperty.call(content || {}, 'json')) {
    textContent = content.json

    if (content?.links?.entries?.inline?.length) {
      textContent = replaceInlineEntries(textContent, content.links)
    }

    if (content?.links?.assets?.hyperlink?.length) {
      textContent = replaceInlineHyperLink(textContent, content.links)
    }

    if (content?.links?.entries?.block?.length) {
      textContent = replaceBlockEntries(textContent, content.links)
    }
  }
  if (Object.hasOwnProperty.call(content || {}, 'fields')) {
    textContent = content.fields.textContent
  } else if (Object.hasOwnProperty.call(content || {}, 'nodeType')) {
    textContent = content
  }
  textContent.content = removeTrailingReturnChar(textContent.content)
  textContent.content = filterInlineContent(textContent.content, inline)

  return { textContent }
}

const RICH_TEXT = {
  [BLOCKS.PARAGRAPH]: { variant: 'bodyLg', component: 'p' },
  [BLOCKS.HEADING_1]: { variant: 'displayXl', component: 'h1' },
  [BLOCKS.HEADING_2]: { variant: 'displayXl', component: 'h2' },
  [BLOCKS.HEADING_3]: { variant: 'displayXl', component: 'h3' },
  [BLOCKS.HEADING_4]: { variant: 'displayXl', component: 'h4' },
  [BLOCKS.HEADING_5]: { variant: 'displayLg', component: 'h5' },
  [BLOCKS.HEADING_6]: { variant: 'headingLg', component: 'h6' },
  [BLOCKS.OL_LIST]: { component: 'ol' },
  [BLOCKS.UL_LIST]: { component: 'ul' },
  [BLOCKS.LIST_ITEM]: { component: 'li' }, //sx properties apply to single child element
  [BLOCKS.HR]: {},
  [BLOCKS.EMBEDDED_ASSET]: {},
  [BLOCKS.TABLE]: { variant: 'bodyLg', component: 'table' },
  [BLOCKS.TABLE_ROW]: { variant: 'bodyLg', component: 'tr' },
  [BLOCKS.TABLE_CELL]: { variant: 'bodyLg', component: 'td' },
  [BLOCKS.TABLE_HEADER_CELL]: { variant: 'bodyLg', component: 'th' },
  [INLINES.HYPERLINK]: { component: 'a' }, // inlines should inherit the font variant
  [INLINES.ASSET_HYPERLINK]: { component: 'a' }, // inlines should inherit the font variant
  // strong is semantic bold
  [MARKS.BOLD]: { variant: 'bodyLg', component: 'strong' },
  // Currently unimplemented in Ascend
  //[MARKS.CODE]: {component: 'code'},
  //[BLOCKS.QUOTE]:{},
}
/**
 * Returns the overridden value for a given rich text enum, or the default value from the RICH_TEXT
 * object.
 *
 * @param {string} richTextEnum - The rich text enum to check for an override.
 * @param {object} overrideObject - The object containing the overrides for the rich text enums.
 * @param {string} key - The key to retrieve the value from the rich text enum or override object.
 * @param {object} richText - The RICH_TEXT object containing the default values for the rich text
 * enums.
 * @returns {string} - The overridden value for the rich text enum, or the default value from the
 * RICH_TEXT object.
 */
const selectOverride = (richTextEnum, overrideObject, key, richText = RICH_TEXT) => {
  return typeof overrideObject === 'object' &&
    Object.hasOwnProperty.call(overrideObject, richTextEnum)
    ? overrideObject[richTextEnum]
    : richText[richTextEnum][key]
}

const parseRichTextLineBreak = (children) => {
  if (Array.isArray(children) && children.length === 1 && children[0] === '') {
    return ['\u00A0']
  }

  function splitBreakLineInline(inputArray) {
    if (!Array.isArray(inputArray)) return inputArray

    return inputArray.flatMap((item, itemIndex) => {
      if (typeof item === 'string') {
        if (item?.includes('\n')) {
          const splitItems = item.split('\n')
          return splitItems.flatMap((splitItem, index) =>
            index < splitItems.length - 1
              ? [splitItem, <br key={`br-${itemIndex}-${index}`} />]
              : [splitItem]
          )
        } else {
          return [item]
        }
      } else {
        return [item]
      }
    })
  }

  return splitBreakLineInline(children).map((child) =>
    typeof child === 'string' ? child.replaceAll(/[\u2028\u2029]/g, '') : child
  )
}

const TypographyStringType = ({
  styleOverride,
  variantOverride,
  mobileVariant,
  mobileVariantOverride,
  typographyContent,
  richText = RICH_TEXT,
  breakpoint,
  responsive,
  props,
}) => {
  const resolvedComponent = props?.component ?? richText[BLOCKS.PARAGRAPH].component

  return responsive ? (
    <AscendResponsiveTypography
      sx={selectOverride(BLOCKS.PARAGRAPH, styleOverride, 'sx')}
      variant={selectOverride(BLOCKS.PARAGRAPH, variantOverride, 'variant')}
      mobileVariant={
        explicitOverrides.includes(mobileVariant)
          ? mobileVariant
          : selectOverride(BLOCKS.PARAGRAPH, mobileVariantOverride, 'mobileVariant')
      }
      breakpoint={breakpoint}
      component={richText[BLOCKS.PARAGRAPH].component}
      {...props}
      className={withHeadingBalance(resolvedComponent, props?.className)}
    >
      {typographyContent}
    </AscendResponsiveTypography>
  ) : (
    <AscendTypography
      sx={selectOverride(BLOCKS.PARAGRAPH, styleOverride, 'sx')}
      variant={selectOverride(BLOCKS.PARAGRAPH, variantOverride, 'variant')}
      component={richText[BLOCKS.PARAGRAPH].component}
      {...props}
      className={withHeadingBalance(resolvedComponent, props?.className)}
    >
      {typographyContent}
    </AscendTypography>
  )
}

/**
 * Create renderNode options for BLOCKS that return similar/identical elements (headers and lists)
 * @param {Object} node      //Is provided by documentToReactComponents
 * @param {Object} children  //Is provided by documentToReactComponents
 * @param {String} blockType      //BLOCKTYPE allowed by documentToReactComponents
 * @param {Boolean} variantToChild //true if variant override should be applied to child component
 * @param {Boolean} styleToChild   //true if style override should be applied to child component
 * @returns {JSX} <JSX element to be displayed >
 *
 * !!! DO NOT _cloneDeep / structuredClone / JSON-roundtrip the `children` array
 * here, and DO NOT mutate `child.props`. React 19 freezes element props and
 * tracks Fibers via `element._owner`; deep-cloning severs that link and the
 * commit phase throws "'run' called with illegal receiver" from
 * runWithFiberInDEV. Always merge new props with `cloneElement(child, extraProps)`
 * via `Children.map`. Re-introducing `_cloneDeep(children)` here will break
 * every Contentful page that renders a heading. (ACX-6337)
 */
function createRenderNodeDefault({
  children,
  blockType,
  responsive,
  mobileVariant,
  breakpoint,
  variantToChild = false,
  styleToChild = false,
  variantOverride,
  mobileVariantOverride,
  styleOverride,
  className,
  richText = RICH_TEXT,
  propsComponent,
  inline = false,
}) {
  //Logic to pass variant and style to children (uses: ie. BLOCK.LIST_ITEM which have nested <p>)
  // React 19 freezes element props and tracks Fibers via element._owner, so the previous
  // approach of _cloneDeep(children) + direct mutation crashed with
  // "'run' called with illegal receiver" during commit. Use Children.map + cloneElement
  // to produce new elements with merged props instead of mutating existing ones.
  let childWithParentProps = children
  if (variantToChild || styleToChild) {
    childWithParentProps = Children.map(children, (child) => {
      if (!isValidElement(child)) return child
      const extraProps = {}
      if (variantToChild) {
        extraProps.variant = selectOverride(BLOCKS[blockType], variantOverride, 'variant', richText)
        if (responsive) {
          extraProps.breakpoint = breakpoint
          extraProps.mobileVariant = explicitOverrides.includes(mobileVariant)
            ? mobileVariant
            : selectOverride(BLOCKS.PARAGRAPH, mobileVariantOverride, 'mobileVariant')
        }
      }
      if (styleToChild) {
        extraProps.sx = selectOverride(BLOCKS[blockType], styleOverride, 'sx', richText)
      }
      return cloneElement(child, extraProps)
    })
  }
  const classNames = [className, propsComponent.className].join(' ')

  // Override component to 'span' when inline is true and blockType is PARAGRAPH
  const componentToUse =
    inline && blockType === 'PARAGRAPH' ? 'span' : richText[BLOCKS[blockType]].component
  const resolvedComponent = propsComponent.component ?? componentToUse

  return responsive ? (
    <AscendResponsiveTypography
      sx={selectOverride(BLOCKS[blockType], styleOverride, 'sx')}
      variant={selectOverride(BLOCKS[blockType], variantOverride, 'variant')}
      mobileVariant={
        explicitOverrides.includes(mobileVariant)
          ? mobileVariant
          : selectOverride(BLOCKS.PARAGRAPH, mobileVariantOverride, 'mobileVariant')
      }
      breakpoint={breakpoint}
      component={componentToUse}
      {...propsComponent}
      className={withHeadingBalance(resolvedComponent, classNames)}
    >
      {parseRichTextLineBreak(childWithParentProps)}
    </AscendResponsiveTypography>
  ) : (
    <AscendTypography
      sx={selectOverride(BLOCKS[blockType], styleOverride, 'sx', richText)}
      variant={selectOverride(BLOCKS[blockType], variantOverride, 'variant', richText)}
      component={componentToUse}
      {...propsComponent}
      className={withHeadingBalance(resolvedComponent, classNames)}
    >
      {parseRichTextLineBreak(childWithParentProps)}
    </AscendTypography>
  )
}

function optionsDefault({
  createRenderNode,
  props,
  styleOverride,
  variantOverride,
  richText = RICH_TEXT,
}) {
  return {
    renderMark: {
      [MARKS.BOLD]: (text) => <strong {...props}>{text}</strong>,
      [MARKS.ITALIC]: (text) => <em {...props}>{text}</em>,
    },
    renderNode: {
      [BLOCKS.PARAGRAPH]: (node, children) => {
        const onlyOneText =
          Array.isArray(node.content) &&
          node.content.length === 1 &&
          node.content[0]?.nodeType === 'text' &&
          typeof node.content[0]?.value === 'string'

        if (onlyOneText) {
          const val = node.content[0].value
          const m = val.match(NO_CRAWL_ID_PLACEHOLDER_REGEX)
          if (m) {
            return createRenderNode(
              node,
              [], // no children => empty <p>
              'PARAGRAPH',
              'font-normal mb-[20px] scroll-mt-[72px] md:text-sm leading-[170%] md:leading-[170%] [&_a]:[font-size:inherit]',
              false,
              false,
              {
                id: m[0],
                ['no-crawl-id']: m[0],
              }
            )
          }
        }

        return createRenderNode(node, children, 'PARAGRAPH')
      },
      [BLOCKS.HEADING_1]: (node, children) => createRenderNode(node, children, 'HEADING_1'),
      [BLOCKS.HEADING_2]: (node, children) => createRenderNode(node, children, 'HEADING_2'),
      [BLOCKS.HEADING_3]: (node, children) => createRenderNode(node, children, 'HEADING_3'),
      [BLOCKS.HEADING_4]: (node, children) => createRenderNode(node, children, 'HEADING_4'),
      [BLOCKS.HEADING_5]: (node, children) => createRenderNode(node, children, 'HEADING_5'),
      [BLOCKS.HEADING_6]: (node, children) => createRenderNode(node, children, 'HEADING_6'),
      [BLOCKS.OL_LIST]: (node, children) => {
        return (
          <ol
            className={
              'typography-ol list-decimal mb-8 [&>li>ol]:list-[upper-alpha] [&>li>ol>li>ol]:list-[lower-roman] [&>li>ol>li>ol>li>ol]:list-[lower-alpha] [&_li_p:only-of-type]:mb-0 [&_li_.typography-ol]:mb-0 [&_li_.typography-ul]:mb-0'
            }
          >
            {children}
          </ol>
        )
      },
      [BLOCKS.UL_LIST]: (node, children) => {
        return (
          <ul
            className={'typography-ul mb-8 [&_li_.typography-ul]:mb-0 [&_li_.typography-ol]:mb-0'}
          >
            {children}
          </ul>
        )
      },
      [BLOCKS.LIST_ITEM]: (node, children) => {
        return <li className={'mb-1 [&_li_p:last-child]:mb-0'}>{children}</li>
      },
      [BLOCKS.HR]: () => {
        return (
          <AscendDivider
            className={'mb-8'}
            sx={selectOverride(BLOCKS.HR, styleOverride, 'sx', richText)}
            {...props}
          />
        )
      },
      [BLOCKS.EMBEDDED_ASSET]: (node) => {
        const imageWidth = _get(node, 'data.target.fields.file.details.image.width')
        const imageHeight = _get(node, 'data.target.fields.file.details.image.height')
        const fileType = _get(node, 'data.target.fields.file.contentType')

        if (fileType === 'application/pdf') {
          return (
            <span className={'items-center flex justify-center'}>
              <PdfViewerClient
                src={getAssetUrl(node?.data?.target?.fields?.file?.url)}
                className="border rounded-md p-4 bg-content-accent-light"
              />
            </span>
          )
        }

        return (
          <span className={'items-center flex justify-center'}>
            <MediaImageStandard
              content={node?.data.target}
              width={imageWidth}
              height={imageHeight}
              className={'object-contain h-full w-full'}
              {...props}
            />
            <p></p>
          </span>
        )
      },
      [BLOCKS.TABLE]: (node, children) => {
        return (
          <div>
            <AscendTableContainer
              component={AscendPaper}
              className={classNames(
                'overflow-scroll border-collapse h-auto mb-8 [&_td_p:last-child]:mb-0 [&_th_p:last-child]:mb-0 [&_td]:min-w-35 [&_th]:min-w-35',
                node?.content[0]?.content?.length < 4 &&
                  'md:[&_td]:w-auto md:[&_th]:w-auto [&_td]:text-center [&_th]:text-center'
              )}
            >
              <AscendTable
                size="small"
                sx={selectOverride(BLOCKS.TABLE, styleOverride, 'sx', richText)}
                variant={selectOverride(BLOCKS.TABLE, variantOverride, 'variant', richText)}
                {...props}
              >
                {children}
              </AscendTable>
            </AscendTableContainer>
          </div>
        )
      },
      [BLOCKS.TABLE_ROW]: (node, children) => {
        const childrenNodeTypes = node?.content?.map((c) => c?.nodeType) ?? []
        if (childrenNodeTypes.includes(BLOCKS.TABLE_HEADER_CELL)) {
          return (
            <AscendTableHead>
              <AscendTableRow>{children}</AscendTableRow>
            </AscendTableHead>
          )
        }
        return (
          <AscendTableBody>
            <AscendTableRow>{children}</AscendTableRow>
          </AscendTableBody>
        )
      },
      [BLOCKS.TABLE_CELL]: (node, children) => {
        return (
          <AscendTableCell className="h-auto border border-solid border-[#DCDCDC] border-collapse">
            {children}
          </AscendTableCell>
        )
      },

      [BLOCKS.TABLE_HEADER_CELL]: (node, children) => {
        return <AscendTableCell className="h-auto">{children}</AscendTableCell>
      },
      [INLINES.HYPERLINK]: (node, children) => {
        return (
          <HyperLinkClient
            node={node}
            props={props}
            styleOverride={styleOverride}
            variantOverride={variantOverride}
            richText={richText}
          >
            {children}
          </HyperLinkClient>
        )
      },
      [INLINES.ASSET_HYPERLINK]: (node, children) => {
        const linkId = node?.content[0]?.value
        const url = getAssetUrl(node?.data?.target?.fields?.file.url || node?.data.url)
        return (
          <AscendLink
            target="_blank"
            sx={selectOverride(INLINES.ASSET_HYPERLINK, styleOverride, 'sx', richText)}
            variant={selectOverride(INLINES.ASSET_HYPERLINK, variantOverride, 'variant', richText)}
            data-testid={`Link-${url}-${linkId}`}
            href={url}
            aria-label={`${children} - link opens in a new tab`}
            {...props}
            component={richText[INLINES.ASSET_HYPERLINK].component}
          >
            {children}
          </AscendLink>
        )
      },
      [INLINES.EMBEDDED_ENTRY]: (node) => {
        // TODO: Handle other types of inline entries. Currently,
        //       node is a data object with no content. Rendering in
        //       an html tag does not work, invalid React child

        const { plainText, styledText, variation, identifier, videoPlayer, uiComponent } = _get(
          node?.data?.target?.fields ? node.data.target.fields : node,
          'data',
          {}
        )
        const { contentType } = _get(node, 'data.target.sys', {})
        if (styledText === 'superscript') {
          return <SuperScript text={plainText} variation={variation} {...props} />
        }
        if (styledText === 'scrollTo') {
          return <ScrollToMarker identifier={identifier} />
        }
        if (styledText === 'scrollFrom') {
          return (
            <ScrollLinkClient
              plainText={plainText}
              identifier={identifier}
              styleOverride={styleOverride}
              variantOverride={variantOverride}
              richText={richText}
              {...props}
            />
          )
        }
        if (videoPlayer && node?.data?.__typename === 'VideoModal') {
          const target = _get(node, 'data', {})
          return <VideoDialogClient content={target} />
        }

        if (contentType?.sys?.id === 'uncrawlableLink') {
          return (
            <UncrawlableLinkClient
              node={node}
              styleOverride={styleOverride}
              variantOverride={variantOverride}
            />
          )
        }
        if (uiComponent === 'Typography') {
          return (
            <Typography
              {...props}
              component="span"
              content={node?.data?.textContent}
              variantOverride={variantOverride}
              styleOverride={styleOverride}
            />
          )
        }
        if (uiComponent === 'TypographyClientSide') {
          return (
            <ClientSideTypographyClient
              textContent={node?.data?.textContent}
              variantOverride={variantOverride}
              styleOverride={styleOverride}
              TypographyComponent={Typography}
              {...props}
            />
          )
        }

        return null
      },
      [BLOCKS.TABLE]: (node, children) => {
        return (
          <div>
            <AscendTableContainer
              component={AscendPaper}
              className={classNames(
                'overflow-scroll border-collapse h-auto mb-8 [&_td_p:last-child]:mb-0 [&_th_p:last-child]:mb-0 [&_td]:min-w-35 [&_th]:min-w-35',

                node?.content[0]?.content?.length < 4 &&
                  'md:[&_td]:w-auto md:[&_th]:w-auto [&_td]:text-center [&_th]:text-center'
              )}
            >
              <AscendTable
                size="small"
                className="table-auto"
                sx={selectOverride(BLOCKS.TABLE, styleOverride, 'sx', richText)}
                variant={selectOverride(BLOCKS.TABLE, variantOverride, 'variant', richText)}
                {...props}
              >
                {children}
              </AscendTable>
            </AscendTableContainer>
          </div>
        )
      },
      [BLOCKS.TABLE_ROW]: (node, children) => {
        const childrenNodeTypes = node?.content?.map((c) => c?.nodeType) ?? []
        if (childrenNodeTypes.includes(BLOCKS.TABLE_HEADER_CELL)) {
          return (
            <AscendTableHead>
              <AscendTableRow>{children}</AscendTableRow>
            </AscendTableHead>
          )
        }
        return (
          <AscendTableBody>
            <AscendTableRow>{children}</AscendTableRow>
          </AscendTableBody>
        )
      },
      [BLOCKS.TABLE_CELL]: (node, children) => {
        return (
          <AscendTableCell className="h-auto border border-solid border-[#DCDCDC] border-collapse">
            {children}
          </AscendTableCell>
        )
      },

      [BLOCKS.TABLE_HEADER_CELL]: (node, children) => {
        return <AscendTableCell className="h-auto">{children}</AscendTableCell>
      },
      [BLOCKS.QUOTE]: (node, children) => {
        return <BlockQuote text={node.content}>{children}</BlockQuote>
      },
    },

    renderText: (text) => {
      // Check if text contains HTML tags (SVG, bold, strong)
      if (
        typeof text === 'string' &&
        (text.includes('<svg') || text.includes('<b>') || text.includes('<strong>'))
      ) {
        const parts = []
        let key = 0

        // Combined regex to match SVG, <b>, and <strong> tags
        const htmlRegex = /(<svg[\s\S]*?<\/svg>|<b>[\s\S]*?<\/b>|<strong>[\s\S]*?<\/strong>)/g
        const segments = text.split(htmlRegex)

        segments.forEach((segment) => {
          const trimmed = segment.trim()

          if (trimmed.startsWith('<svg')) {
            // This is an SVG segment — sanitize to prevent XSS from CMS content
            parts.push(
              <span
                key={`svg-${key++}`}
                className="inline-flex items-center align-middle mx-1"
                dangerouslySetInnerHTML={{ __html: sanitizeSvg(segment) }}
              />
            )
          } else if (trimmed.startsWith('<b>') || trimmed.startsWith('<strong>')) {
            // This is a bold segment - extract text and render as <strong>
            const boldText = segment.replace(/<\/?b>|<\/?strong>/g, '')
            parts.push(<strong key={`bold-${key++}`}>{boldText}</strong>)
          } else if (segment) {
            // This is regular text
            parts.push(segment)
          }
        })

        return parts.length > 0 ? parts : text
      }

      return text
    },
  }
}

/**
 * Render Contentful Typography components from Contentful Rich Text
 * To build the variantOverride object you will need to import import { BLOCKS, MARKS, INLINES } from '@contentful/rich-text-types'
 * @param {Object} content
 * @param {Object} variantOverride object of rich-text-types as keys and Ascend Variants as the value
 * @param {Object} styleOverride object of rich-text-types as keys and a MUI "sx" object as the value
 * @param {String} mobileVariant object of rich-text-types as keys and a MUI "sx" object as the value
 * @param {String} breakpoint object of rich-text-types as keys and a MUI "sx" object as the value
 * @param {Boolean} responsive determines to use standard typography or responsive
 * @returns
 */
function Typography({
  content: typographyContent = '',
  variantOverride,
  mobileVariantOverride,
  styleOverride,
  mobileVariant,
  breakpoint,
  responsive,
  inline,
  ...props
}) {
  if (typeof typographyContent === 'string') {
    return TypographyStringType({
      styleOverride,
      variantOverride,
      mobileVariantOverride,
      typographyContent,
      mobileVariant,
      breakpoint,
      responsive,
      props,
    })
  }
  // otherwise must be an object
  const { textContent } = handleContentToMapper(typographyContent, inline)

  const createRenderNode = (
    node,
    children,
    blockType,
    className = 'typography-default',
    variantToChild = false,
    styleToChild = false,
    extraProps = {}
  ) => {
    const fallbackTestId = _get(node, 'content[0].value')
      ? `${_get(node, 'content[0].value')}`.slice(0, 50)
      : 'no-id-needed'

    const propsComponent = {
      ['data-testid']: fallbackTestId,
      'no-crawl-id': extraProps['no-crawl-id'] ?? undefined,
      ...props,
    }

    return createRenderNodeDefault({
      children,
      blockType,
      variantToChild,
      styleToChild,
      variantOverride,
      mobileVariantOverride,
      styleOverride,
      className,
      mobileVariant,
      breakpoint,
      responsive,
      propsComponent,
      inline,
    })
  }

  const options = optionsDefault({ createRenderNode, props, styleOverride, variantOverride })

  return textContent?.nodeType || textContent?.data
    ? documentToReactComponents(textContent || [], options)
    : null
}

export default Typography
export {
  Typography,
  removeTrailingReturnChar,
  RICH_TEXT,
  handleContentToMapper,
  selectOverride,
  parseRichTextLineBreak,
  TypographyStringType,
  createRenderNodeDefault,
  optionsDefault,
}
